import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { FiSearch, FiUser, FiShoppingBag, FiMenu, FiX, FiLogOut, FiGrid, FiMoon, FiSun } from 'react-icons/fi';

const Navbar = () => {
  const { user, logout, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [mobileMenu, setMobileMenu] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [darkMode, setDarkMode] = useState(() => {
    const storedTheme = localStorage.getItem('theme');
    if (storedTheme) {
      return storedTheme === 'dark';
    }
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  useEffect(() => {
    document.body.classList.toggle('dark-mode', darkMode);
    localStorage.setItem('theme', darkMode ? 'dark' : 'light');
  }, [darkMode]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/products?search=${searchQuery}`);
      setSearchQuery('');
      setMobileMenu(false);
    }
  };

  return (
    <nav className="bg-[#386646] shadow-card sticky top-0 z-50 border-b border-[#2f5438]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center space-x-2 group"
          >
            <div className="bg-[#F27D16] p-2 rounded-[10px] group-hover:scale-110 transition-transform duration-300">
              <FiShoppingBag className="text-white text-xl" />
            </div>
            <span className="text-xl font-display font-bold text-white">
              RentHub
            </span>
          </Link>

          {/* Search Bar - Desktop */}
          <form 
            onSubmit={handleSearch}
            className="hidden md:flex items-center flex-1 max-w-md mx-8"
          >
            <div className="relative w-full">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search products..."
                className="input-field pl-10"
              />
              <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[rgba(34,34,34,0.5)]" />
            </div>
          </form>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-6">
            <button
              onClick={() => setDarkMode((prevMode) => !prevMode)}
              className="w-10 h-10 rounded-full bg-[#FFF9D4] text-[#386646] hover:bg-[#FCD06B] hover:text-[#2f5438] transition-colors duration-200 flex items-center justify-center"
              aria-label={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
              title={darkMode ? 'Light mode' : 'Dark mode'}
            >
              {darkMode ? <FiSun className="text-lg" /> : <FiMoon className="text-lg" />}
            </button>

            <Link
              to="/products"
              className="text-white hover:text-[#FCD06B] font-medium transition-colors duration-200 relative group"
            >
              Browse
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#FCD06B] group-hover:w-full transition-all duration-300"></span>
            </Link>

            {isAuthenticated ? (
              <>
                <Link
                  to={user?.role === 'seller' ? '/seller/dashboard' : '/buyer/bookings'}
                  className="flex items-center space-x-2 text-white hover:text-[#FCD06B] transition-colors duration-200"
                >
                  <FiGrid className="text-lg" />
                  <span className="font-medium">Dashboard</span>
                </Link>

                <div className="relative group">
                  <button className="flex items-center space-x-2 text-white hover:text-[#FCD06B] transition-colors duration-200">
                    <div className="w-8 h-8 rounded-full bg-[#F27D16] flex items-center justify-center text-white font-medium text-sm">
                      {user?.name?.charAt(0).toUpperCase()}
                    </div>
                    <span className="font-medium text-sm">{user?.name}</span>
                  </button>

                  {/* Dropdown */}
                  <div className="absolute right-0 mt-2 w-48 bg-[#FFF9D4] rounded-[12px] shadow-hover opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 py-2 border border-[#FCD06B]">
                    <Link
                      to="/profile"
                      className="flex items-center space-x-2 px-4 py-2 text-[#2D2D2D] hover:bg-[#FFF5B8] hover:text-[#F27D16] transition-colors"
                    >
                      <FiUser />
                      <span>Profile</span>
                    </Link>
                    <button
                      onClick={handleLogout}
                      className="flex items-center space-x-2 px-4 py-2 text-[#2D2D2D] hover:bg-[#FFF5B8] hover:text-[#F27D16] transition-colors w-full text-left"
                    >
                      <FiLogOut />
                      <span>Logout</span>
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  className="text-white hover:text-[#FCD06B] font-medium transition-colors duration-200"
                >
                  Login
                </Link>
                <Link
                  to="/register"
                  className="btn-primary"
                >
                  Sign Up
                </Link>
              </>
            )}
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setMobileMenu(!mobileMenu)}
            className="md:hidden text-white hover:text-[#FCD06B] transition-colors"
          >
            {mobileMenu ? <FiX className="text-2xl" /> : <FiMenu className="text-2xl" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenu && (
        <div className="md:hidden bg-[#386646] border-t border-[#2f5438] animate-fadeIn">
          <div className="px-4 py-4 space-y-4">
            {/* Mobile Search */}
            <form onSubmit={handleSearch}>
              <div className="relative">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search products..."
                  className="input-field pl-10"
                />
                <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[rgba(34,34,34,0.5)]" />
              </div>
            </form>

            <Link
              to="/products"
              className="block text-white hover:text-[#FCD06B] font-medium py-2 transition-colors"
              onClick={() => setMobileMenu(false)}
            >
              Browse
            </Link>

            <button
              onClick={() => setDarkMode((prevMode) => !prevMode)}
              className="flex items-center space-x-2 text-white hover:text-[#FCD06B] font-medium py-2 transition-colors w-full"
            >
              {darkMode ? <FiSun className="text-lg" /> : <FiMoon className="text-lg" />}
              <span>{darkMode ? 'Light Mode' : 'Dark Mode'}</span>
            </button>

            {isAuthenticated ? (
              <>
                <Link
                  to={user?.role === 'seller' ? '/seller/dashboard' : '/buyer/bookings'}
                  className="flex items-center space-x-2 text-white hover:text-[#FCD06B] font-medium py-2 transition-colors"
                  onClick={() => setMobileMenu(false)}
                >
                  <FiGrid className="text-lg" />
                  <span>Dashboard</span>
                </Link>

                <Link
                  to="/profile"
                  className="flex items-center space-x-2 text-white hover:text-[#FCD06B] font-medium py-2 transition-colors"
                  onClick={() => setMobileMenu(false)}
                >
                  <FiUser />
                  <span>Profile</span>
                </Link>

                <button
                  onClick={() => {
                    handleLogout();
                    setMobileMenu(false);
                  }}
                  className="flex items-center space-x-2 text-white hover:text-[#FCD06B] font-medium py-2 transition-colors w-full"
                >
                  <FiLogOut />
                  <span>Logout</span>
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  className="block text-white hover:text-[#FCD06B] font-medium py-2 transition-colors"
                  onClick={() => setMobileMenu(false)}
                >
                  Login
                </Link>
                <Link
                  to="/register"
                  className="btn-primary block text-center"
                  onClick={() => setMobileMenu(false)}
                >
                  Sign Up
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
